import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  SlidersHorizontal as Filters,
  Plus,
  Pencil,
  Briefcase,
  Calendar,
  MoreVertical,
  Activity,
  ArrowUpRight,
  Trash2,
  Eye,
  Loader2,
  X,
  Inbox
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './Projects.module.css';
import api from '../../api/axios';
import Pagination from '../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const Projects = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainContentRef = useRef(null);
  const isInitialMount = useRef(true);
  const [projects, setProjects] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    customer_id: '',
    status: '',
    priority: ''
  });
  const [filterData, setFilterData] = useState({
    customers: []
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  // Permission guards
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create projects');
  const canEdit = isAdmin || userPermissions.includes('edit projects');
  const canDelete = isAdmin || userPermissions.includes('delete projects');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchTerm !== debouncedSearch) {
        setPagination(prev => ({ ...prev, current_page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const custRes = await api.get('/customers');
      setFilterData({
        customers: custRes.data.data.data || custRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      customer_id: '',
      status: '',
      priority: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const fetchProjects = async () => {
    try {
      let url = `/projects?page=${pagination.current_page}&per_page=${pagination.per_page}`;
      if (debouncedSearch) {
        url += `&search=${encodeURIComponent(debouncedSearch)}`;
      }
      if (filters.customer_id) url += `&customer_id=${filters.customer_id}`;
      if (filters.status !== '') url += `&status=${filters.status}`;
      if (filters.priority !== '') url += `&priority=${filters.priority}`;

      const response = await api.get(url);
      const { data, current_page, last_page, total, per_page } = response.data.data;
      setProjects(data);
      setPagination({ current_page, last_page, total, per_page });
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Scroll the main content container to top on page change
    const scrollTimeout = setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);

    return () => clearTimeout(scrollTimeout);
  }, [pagination.current_page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
    }
  };

  const handleDelete = async (project) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Project</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Project Record?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete <strong>${project.project_code}: ${project.project_name}</strong>?<br/>
          This action cannot be undone and may affect linked records.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/projects/${project.id}`);
          if (response.data.success) {
            toast.success('Project deleted successfully');
            fetchProjects();
          }
        } catch (error) {
          console.error('Error deleting project:', error);
          toast.error(error.response?.data?.message || 'Failed to delete project');
        }
      }
    });
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleAddClick = () => {
    navigate('/projects/create');
  };

  const handleEditClick = (project) => {
    navigate(`/projects/edit/${project.id}`);
  };

  const getStatusClass = (status) => {
    return styles[`status-${status.toLowerCase()}`] || '';
  };

  const getPriorityClass = (priority) => {
    return styles[`priority-${priority.toLowerCase()}`] || '';
  };

  return (
    <div className={styles['projects-layout']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-area']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />
          <div className={styles['page-header']}>
            <div className={styles['header-title']}>
              <h1>Projects</h1>
              <p>Manage and track all electrical contracting projects across various stages.</p>
            </div>
            {canCreate && (
              <button className={styles['add-btn']} onClick={handleAddClick}>
                <Plus size={20} />
                <span>Add Project</span>
              </button>
            )}
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search projects by name, code or client..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.customer_id || filters.status !== '' || filters.priority !== '') && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Projects</h3>
                  <p>Filter records by client, status, or priority.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Client</label>
                  <CustomSelect
                    value={filters.customer_id ? {
                      value: filters.customer_id,
                      label: filterData.customers.find(d => d.id === filters.customer_id)?.customer_name
                    } : null}
                    onChange={(opt) => handleFilterChange('customer_id', opt ? opt.value : '')}
                    options={filterData.customers.map(d => ({ value: d.id, label: d.customer_name }))}
                    placeholder="All Clients"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status !== '' ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1)
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: 'planning', label: 'Planning' },
                      { value: 'execution', label: 'Execution' },
                      { value: 'delayed', label: 'Delayed' },
                      { value: 'completed', label: 'Completed' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Priority</label>
                  <CustomSelect
                    value={filters.priority !== '' ? {
                      value: filters.priority,
                      label: filters.priority.charAt(0).toUpperCase() + filters.priority.slice(1)
                    } : null}
                    onChange={(opt) => handleFilterChange('priority', opt ? opt.value : '')}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' }
                    ]}
                    placeholder="All Priorities"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['project-table']}>
                <thead>
                  <tr>
                    <th>Project & ID</th>
                    <th>Client</th>
                    <th>Work Order Date</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th className={styles['actions-cell']}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <tr key={project.id}>
                        <td>
                          <div className={styles['project-info']}>
                            <span className={styles['project-name']}>{project.project_name}</span>
                            <span className={styles['project-id']}>{project.project_code}</span>
                          </div>
                        </td>
                        <td>{project.customer?.customer_name || 'N/A'}</td>
                        <td>
                          <div className={styles['timeline-info']}>
                            {project.work_order_date ? new Date(project.work_order_date).toLocaleDateString() : 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div className={`${styles['priority-badge']} ${getPriorityClass(project.priority)}`}>
                            <span className={styles['priority-dot']}></span>
                            {project.priority}
                          </div>
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${getStatusClass(project.status)}`}>
                            {project.status}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === project.id ? styles['active'] : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleActionMenu(project.id, e);
                              }}
                            >
                              <MoreVertical size={18} />
                            </button>

                            {openMenuIndex === project.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{ top: menuPosition.top, left: menuPosition.left }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {canEdit && (
                                  <button className={styles['dropdown-item']} onClick={() => {
                                    handleEditClick(project);
                                    setOpenMenuIndex(null);
                                  }}>
                                    <Pencil size={18} /> Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <>
                                    <div className={styles['dropdown-divider']}></div>
                                    <button
                                      className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                      onClick={() => handleDelete(project)}
                                    >
                                      <Trash2 size={18} /> Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : !loading && (
                    <NoData message="No projects found matching your search." colSpan={6} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['project-cards']}>
              {projects.length > 0 ? (
                projects.map((project) => (
                  <div key={project.id} className={styles['project-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-details']}>
                        <span className={styles['card-code']}>{project.project_code}</span>
                        <h3>{project.project_name}</h3>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === project.id ? styles['active'] : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(project.id, e);
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openMenuIndex === project.id && (
                          <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{ top: menuPosition.top, left: menuPosition.left }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {canEdit && (
                              <button className={styles['dropdown-item']} onClick={() => {
                                handleEditClick(project);
                                setOpenMenuIndex(null);
                              }}>
                                <Pencil size={18} /> Edit
                              </button>
                            )}
                            {canDelete && (
                              <>
                                <div className={styles['dropdown-divider']}></div>
                                <button
                                  className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                  onClick={() => handleDelete(project)}
                                >
                                  <Trash2 size={18} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles['card-stats']}>
                      <div className={styles['stat-item']}>
                        <span className={styles['stat-label']}>Client</span>
                        <span className={styles['stat-value']}>{project.customer?.customer_name || 'N/A'}</span>
                      </div>
                      <div className={styles['stat-item']}>
                        <span className={styles['stat-label']}>Priority</span>
                        <span className={`${styles['priority-badge']} ${getPriorityClass(project.priority)}`}>
                          {project.priority}
                        </span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <span className={`${styles['status-badge']} ${getStatusClass(project.status)}`}>
                        {project.status}
                      </span>
                      <div className={styles['card-timeline']}>
                        <Calendar size={14} />
                        <span>{project.work_order_date ? new Date(project.work_order_date).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : !loading && (
                <NoData message="No records." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            />
          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Projects;
